'use client';
import React, { useState } from 'react';
import { X, Flag, CheckCircle, AlertCircle } from 'lucide-react';

const REPORT_REASONS = [
  'Harassment',
  'Abusive behavior',
  'Spam',
  'Fake profile',
  'Fake review',
  'Unsafe conduct',
  'Scam / suspicious activity',
  'Inappropriate content',
];

type ReportState = 'idle' | 'submitting' | 'success' | 'error';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  contentType?: 'profile' | 'service' | 'review' | 'conversation' | 'listing' | 'resource';
  contentLabel?: string;
}

export default function ReportModal({
  isOpen,
  onClose,
  contentType = 'profile',
  contentLabel,
}: ReportModalProps) {
  const [selectedReason, setSelectedReason] = useState('');
  const [notes, setNotes] = useState('');
  const [state, setState] = useState<ReportState>('idle');

  if (!isOpen) return null;

  const contentTypeLabel = {
    profile: 'profile',
    service: 'service listing',
    review: 'review',
    conversation: 'conversation',
    listing: 'marketplace listing',
    resource: 'resource',
  }[contentType];

  function handleSubmit() {
    if (!selectedReason) return;
    setState('submitting');
    setTimeout(() => {
      setState('success');
    }, 1200);
  }

  function handleClose() {
    setState('idle');
    setSelectedReason('');
    setNotes('');
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      <div
        className="absolute inset-0 backdrop-blur-sm"
        style={{ background: 'rgba(7,6,12,0.80)' }}
        onClick={handleClose}
      />
      <div
        className="relative w-full max-w-md rounded-2xl shadow-2xl"
        style={{
          background: '#0C0A16',
          border: '1px solid rgba(255,255,255,0.10)',
          boxShadow: '0 24px 80px rgba(0,0,0,0.60)',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-5 border-b"
          style={{ borderColor: 'rgba(255,255,255,0.08)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(255,107,107,0.10)', border: '1px solid rgba(255,107,107,0.20)' }}
            >
              <Flag size={14} style={{ color: '#FF6B6B' }} />
            </div>
            <div>
              <h2 className="font-display font-semibold text-[15px]" style={{ color: '#F2F4FF' }}>
                Report {contentTypeLabel}
              </h2>
              {contentLabel && (
                <p className="text-[12px]" style={{ color: '#A7A3C4' }}>{contentLabel}</p>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-lg transition-all duration-150 hover:bg-white/[0.06] active:scale-95"
            style={{ color: '#A7A3C4' }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {state === 'success' ? (
            <div className="flex flex-col items-center text-center py-6">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
                style={{ background: 'rgba(45,212,160,0.08)', border: '1px solid rgba(45,212,160,0.20)' }}
              >
                <CheckCircle size={24} style={{ color: '#2DD4A0' }} />
              </div>
              <h3 className="font-display font-semibold text-[16px] mb-2" style={{ color: '#F2F4FF' }}>
                Report submitted
              </h3>
              <p className="text-[13.5px] leading-relaxed" style={{ color: '#A7A3C4' }}>
                Thank you for helping keep Panorama safe. Our team will review your report discreetly.
              </p>
              <button
                onClick={handleClose}
                className="btn-secondary mt-6 px-6 py-2.5 text-[13px]"
              >
                Done
              </button>
            </div>
          ) : state === 'error' ? (
            <div className="flex flex-col items-center text-center py-6">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
                style={{ background: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.20)' }}
              >
                <AlertCircle size={24} style={{ color: '#FF6B6B' }} />
              </div>
              <h3 className="font-display font-semibold text-[16px] mb-2" style={{ color: '#F2F4FF' }}>
                Something went wrong
              </h3>
              <p className="text-[13.5px] leading-relaxed" style={{ color: '#A7A3C4' }}>
                We couldn't submit your report right now. Please try again in a moment.
              </p>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setState('idle')} className="btn-primary px-5 py-2.5 text-[13px]">
                  Try again
                </button>
                <button onClick={handleClose} className="btn-secondary px-5 py-2.5 text-[13px]">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-[13.5px] mb-5 leading-relaxed" style={{ color: '#A7A3C4' }}>
                Select a reason for your report. All reports are reviewed privately by our team.
              </p>

              {/* Reason selection */}
              <div className="space-y-2 mb-5">
                {REPORT_REASONS.map((reason) => (
                  <button
                    key={reason}
                    onClick={() => setSelectedReason(reason)}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all duration-150 hover:bg-white/[0.04]"
                    style={{
                      background: selectedReason === reason ? 'rgba(255,107,107,0.08)' : 'rgba(255,255,255,0.02)',
                      border: `1px solid ${selectedReason === reason ? 'rgba(255,107,107,0.25)' : 'rgba(255,255,255,0.06)'}`,
                    }}
                  >
                    <div
                      className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center"
                      style={{
                        border: `2px solid ${selectedReason === reason ? '#FF6B6B' : 'rgba(167,163,196,0.30)'}`,
                        background: selectedReason === reason ? '#FF6B6B' : 'transparent',
                      }}
                    >
                      {selectedReason === reason && (
                        <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#0C0A16' }} />
                      )}
                    </div>
                    <span className="text-[13.5px]" style={{ color: selectedReason === reason ? '#F2F4FF' : '#A7A3C4' }}>
                      {reason}
                    </span>
                  </button>
                ))}
              </div>

              {/* Notes */}
              <div className="mb-6">
                <label className="block text-[12px] font-medium mb-2" style={{ color: 'rgba(167,163,196,0.70)' }}>
                  Additional notes <span style={{ color: 'rgba(167,163,196,0.40)' }}>(optional)</span>
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional context that may help our review..."
                  rows={3}
                  className="input-neon w-full resize-none text-[13.5px] leading-relaxed"
                  style={{ minHeight: '80px' }}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={handleSubmit}
                  disabled={!selectedReason || state === 'submitting'}
                  className="btn-primary flex-1 py-2.5 text-[13.5px] disabled:opacity-40 disabled:cursor-not-allowed"
                  style={selectedReason ? { background: 'rgba(255,107,107,0.15)', borderColor: 'rgba(255,107,107,0.30)', color: '#FF6B6B' } : {}}
                >
                  {state === 'submitting' ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-3.5 h-3.5 rounded-full border-2 border-current border-t-transparent animate-spin" />
                      Submitting…
                    </span>
                  ) : 'Submit report'}
                </button>
                <button onClick={handleClose} className="btn-secondary px-5 py-2.5 text-[13.5px]">
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
